// The operator's copy of their own operation.
//
// There is no server holding a second copy of any of this — that is the
// product, not an omission — so the console has to be able to hand the
// operator a file and take one back. Pure functions, apart from the
// browser: reading a backup is the one path in this app where a bad file
// must produce a sentence rather than a half-restored fleet, and that is
// worth testing rather than clicking.
//
// **A backup is the keys.** The issuer seed signs jobs; the route roots
// are what term links are made from; the console's device key is what
// opens what it sent. Encrypting the file would mean a passphrase this
// app would then have to help people recover, which is an account system
// with extra steps. So it is written in the clear and the screen says
// exactly what it is worth to whoever picks it up.

import { emptyState, type FleetState } from "./state";

export const BACKUP_KIND = "wayfind.fleet.backup";
export const BACKUP_VERSION = 1;

export interface BackupFile {
  kind: typeof BACKUP_KIND;
  version: typeof BACKUP_VERSION;
  takenUnixSeconds: number;
  state: FleetState;
}

/** What a backup is called on disk. Dated, because operators keep several. */
export function backupFilename(takenUnixSeconds: number): string {
  const stamp = new Date(takenUnixSeconds * 1000).toISOString().slice(0, 10);
  return `wayfind-${stamp}.fleetbackup`;
}

export function encodeBackup(state: FleetState, takenUnixSeconds: number): string {
  const file: BackupFile = {
    kind: BACKUP_KIND,
    version: BACKUP_VERSION,
    takenUnixSeconds,
    state
  };
  return JSON.stringify(file, null, 2);
}

/** What reading a file produced: an operation, or a sentence about why not. */
export type BackupRead =
  | { ok: true; state: FleetState; takenUnixSeconds: number }
  | { ok: false; reason: string };

/**
 * Reads a backup file.
 *
 * Refuses rather than salvages. A half-restored operation — a roster
 * without the issuer seed that sealed to it, jobs whose route roots are
 * gone — is worse than a refusal, because the operator carries on
 * believing they have their fleet back and finds out at the first
 * handover.
 */
export function readBackup(text: string): BackupRead {
  let parsed: Partial<BackupFile>;
  try {
    parsed = JSON.parse(text) as Partial<BackupFile>;
  } catch {
    return { ok: false, reason: "That file is not a console backup — it is not even JSON." };
  }
  if (!parsed || typeof parsed !== "object" || parsed.kind !== BACKUP_KIND) {
    return { ok: false, reason: "That is not a console backup." };
  }
  if (parsed.version !== BACKUP_VERSION) {
    return {
      ok: false,
      reason: `That backup is version ${String(parsed.version)}, and this console writes `
        + `version ${BACKUP_VERSION}.`
    };
  }
  const state = parsed.state;
  if (!state || typeof state !== "object" || state.version !== 3) {
    return {
      ok: false,
      reason: "That backup was written by a version of this console that stored things "
        + "differently, and half-restoring an operation is worse than not restoring it."
    };
  }
  // Merged over a fresh shape for the same reason `loadState` does: a
  // field this build added must arrive present and empty rather than as
  // `undefined` two components down. Arrays are checked because a
  // hand-edited file is a real thing and `state.jobs.map` on a string is
  // a white screen.
  const restored: FleetState = {
    ...emptyState(),
    ...state,
    jobs: Array.isArray(state.jobs) ? state.jobs : [],
    devices: Array.isArray(state.devices) ? state.devices : [],
    routes: Array.isArray(state.routes) ? state.routes : [],
    offers: Array.isArray(state.offers) ? state.offers : []
  };
  return {
    ok: true,
    state: restored,
    takenUnixSeconds: Number(parsed.takenUnixSeconds) || 0
  };
}
